import Constants from '@/Constants';
import List from '@/models/List';
import { Module } from '@/models/Module';
import { Program } from '@/models/Program';
import { ProgramCourseRequest } from '@/models/ProgramCourseRequest';
import { ProgramListStudentViewRequest } from '@/models/ProgramListStudentViewRequest';
import { ProgramPaginationData } from '@/models/ProgramPaginationData';
import { ProgramRequest } from '@/models/ProgramRequest';
import { courseService } from '@/services/CourseService';
import http from '@/services/HttpService';
import { LocaleData } from '@/translations/LocaleData';
import { AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { call, delay, put, select } from 'redux-saga/effects';
import { PayloadAction } from 'typesafe-actions';
import { ApplicationState } from '..';
import {
    addCourseInProgramFailure,
    addCourseInProgramSuccess,
    createProgramCourseSuccess,
    createProgramFailure,
    createProgramSuccess,
    getProgramClassStudentViewFailure,
    getProgramClassStudentViewSucess,
    getProgramCoursesFailure,
    getProgramCoursesRequest,
    getProgramCoursesSuccess,
    getProgramFailure,
    getProgramListStudentViewFailure,
    getProgramPrerequisitesFailure,
    getProgramPrerequisitesSuccess,
    getProgramSearchedFailure,
    getProgramSearchedSuccess,
    getProgramsFailure,
    getProgramsSuccess,
    getProgramSuccess,
    increaseProgramViewsFailure,
    increaseProgramViewsSuccess,
    programsCategorizedSuccess,
    programsCompletedSuccess,
    programsEssentialClear,
    programsEssentialSuccess,
    programsFavoriteClear,
    programsFavoriteSuccess,
    programsInProgressClear,
    programsInProgressSuccess,
    programsLastPublishedClear,
    programsLastPublishedSuccess,
    programsTrainingsSuccess,
    publishProgramFailure,
    publishProgramSuccess,
    removeCourseFromProgramFailure,
    removeCourseFromProgramSuccess,
    reorderProgramCourseFailure,
    reorderProgramCourseSuccess,
    setProgramPrerequisitesFailure,
    setProgramPrerequisitesSuccess,
    setProgramReadyFailure,
    setProgramReadySuccess,
    updateProgramClassStudentViewFailure,
    updateProgramClassStudentViewSuccess,
    updateProgramFailure,
    updateProgramSuccess,
} from './action';
import { ProgramSearchedRequest, ProgramTypes } from './types';

let requestCounter: number = 0;
export function* getProgramsRequest(action: PayloadAction<ProgramTypes.GET_PROGRAMS_REQUEST, ProgramRequest>) {
    try {
        yield delay(requestCounter++ * Constants.SAGA_DELAY);

        const {
            organizationId,
            applicationId,
            onlyHasProgramClass,
            moduleId,
            isActive,
            status,
            courseId,
            name,
            locale,
            withCourseAmount,
            sumWorkload,
            orderBy,
            minWorkload,
            maxWorkload,
            withClassAmount,
        } = action.payload;
        let queryParams: string = `?applicationId=${applicationId}&organizationId=${organizationId}&moduleId=${moduleId}`;

        if (onlyHasProgramClass !== undefined) {
            queryParams += `&onlyHasProgramClass=${onlyHasProgramClass}`;
        }

        if (isActive !== undefined) {
            queryParams += `&isActive=${isActive}`;
        }

        if (status !== undefined) {
            queryParams += `&status=${status}`;
        }

        if (courseId !== undefined) {
            queryParams += `&courseId=${courseId}`;
        }

        if (withCourseAmount) {
            queryParams += `&withCourseAmount=${withCourseAmount}`;
        }

        if (sumWorkload) {
            queryParams += `&sumWorkload=${sumWorkload}`;
        }

        if (name !== undefined && name.length > 0) {
            queryParams += `&name=${name}`;
        }

        if (locale !== undefined) {
            queryParams += `&locale=${locale}`;
        }

        if (orderBy !== undefined) {
            queryParams += `&orderBy=${orderBy}`;
        }

        if (minWorkload !== undefined) {
            queryParams += `&minWorkload=${minWorkload}`;
        }

        if (maxWorkload !== undefined) {
            queryParams += `&maxWorkload=${maxWorkload}`;
        }

        if (withClassAmount !== undefined) {
            queryParams += `&withClassAmount=${withClassAmount}`;
        }

        const { data } = yield call(http.get, Constants.LMS_PROGRAMS_URI + queryParams);
        yield put(getProgramsSuccess(data));
    } catch (err) {
        yield put(getProgramsFailure(err));
    } finally {
        requestCounter--;
    }
}

export function* getProgramRequest(action: PayloadAction<ProgramTypes.GET_PROGRAM_REQUEST, number>) {
    try {
        const response = yield call(http.get, Constants.LMS_PROGRAM_URI.replace('%p', action.payload.toString()));
        yield put(getProgramSuccess(response.data));
    } catch (err) {
        yield put(getProgramFailure(err));
    }
}

export function* createProgram(action: PayloadAction<ProgramTypes.CREATE_PROGRAM_REQUEST, Program>) {
    try {
        const response = yield call(http.post, Constants.LMS_PROGRAMS_URI, action.payload);
        yield put(createProgramSuccess(response.data));
    } catch (err) {
        yield put(createProgramFailure(err));
    }
}

export function* updateProgram(action: PayloadAction<ProgramTypes.UPDATE_PROGRAM_REQUEST, Program>) {
    try {
        const { id } = action.payload;
        yield call(http.put, Constants.LMS_PROGRAM_URI.replace('%p', id.toString()), action.payload);
        yield put(updateProgramSuccess());
    } catch (err) {
        yield put(updateProgramFailure(err));
    }
}

export function* reorderProgramCourse(action: PayloadAction<ProgramTypes.REORDER_PROGRAM_COURSE_REQUEST, ProgramCourseRequest>) {
    try {
        const { programId, courseId, order, prerequisites } = action.payload;
        let path: string = Constants.FLOWLMS_PROGRAM_COURSE_REORDER_URI.replace('%p', programId.toString())
            .replace('%c', courseId.toString())
            .replace('%o', order.toString());
        if (prerequisites) {
            path += '?prerequisites=true';
        }
        yield call(http.put, path);
        yield put(reorderProgramCourseSuccess());
        yield put(getProgramCoursesRequest(programId));
    } catch (err) {
        yield put(reorderProgramCourseFailure(err));
    }
}

export function* removeCourseFromProgram(
    action: PayloadAction<ProgramTypes.REMOVE_PROGRAM_COURSES_REQUEST, { programId: number; courseId: number; prerequisites: boolean }>,
) {
    try {
        const { programId, courseId, prerequisites } = action.payload;
        let path = Constants.FLOWLMS_PROGRAM_COURSES_URI.replace('%p', programId.toString()).replace('%c', courseId.toString());
        if (prerequisites) {
            path += '?prerequisites=true';
        }
        yield call(http.delete, path);
        yield put(removeCourseFromProgramSuccess());
    } catch (err) {
        yield put(removeCourseFromProgramFailure(err));
    }
}

export function* getProgramCourses(action: PayloadAction<ProgramTypes.GET_PROGRAM_COURSES_LIST_REQUEST, number>) {
    try {
        const response = yield call(http.get, Constants.LMS_PROGRAM_COURSES_URI.replace('%p', action.payload.toString()));
        yield put(getProgramCoursesSuccess(response.data));
    } catch (err) {
        yield put(getProgramCoursesFailure(err));
    }
}

export function* getProgramPrerequisites(action: PayloadAction<ProgramTypes.GET_PROGRAM_PREREQUISITES_REQUEST, number>) {
    try {
        const response = yield call(http.get, Constants.LMS_PROGRAM_PREREQUISITES.replace('%p', action.payload.toString()));
        yield put(getProgramPrerequisitesSuccess(response.data && response.data.length > 0));
    } catch (err) {
        yield put(getProgramPrerequisitesFailure(err));
    }
}

export function* addCourseInProgram(
    action: PayloadAction<ProgramTypes.ADD_COURSE_IN_PROGRAM_REQUEST, { programId: string; course: List; prerequisites: boolean }>,
) {
    try {
        const { programId, course, prerequisites } = action.payload;
        let path = Constants.FLOWLMS_PROGRAM_COURSES_URI.replace('%p', programId).replace('%c', `${course.id}`);
        if (prerequisites) {
            path += '?prerequisites=true';
        }
        yield call(http.post, path, action.payload.course);
        yield put(addCourseInProgramSuccess());
    } catch (error) {
        yield put(addCourseInProgramFailure(error));
    }
}

export function* createProgramCourse(action: PayloadAction<ProgramTypes.CREATE_PROGRAM_COURSE_REQUEST, ProgramCourseRequest & { course: List }>) {
    try {
        const { courseId } = action.payload;
        const { programId } = action.payload;
        const { course } = action.payload;
        const { data } = yield call(
            http.post,
            `${Constants.FLOWLMS_PROGRAM_COURSE_URI.replace('%p', programId.toString()).replace('%c', courseId.toString())}`,
            course,
        );
        yield put(createProgramCourseSuccess(data));
    } catch (err) {
        yield put(getProgramCoursesFailure(err));
    }
}

export function* publishProgram(action: PayloadAction<ProgramTypes.PUBLISH_PROGRAM_REQUEST, number>) {
    const strings: LocaleData = yield select((state: ApplicationState) => state.locale.data);

    try {
        yield call(http.post, `${Constants.LMS_PROGRAMS_URI}/${action.payload}/publish`);
        yield put(publishProgramSuccess(action.payload));
    } catch (err) {
        toast.error(strings.toastMessages.LIST_PUBLISHED_ERROR);
        yield put(publishProgramFailure(err));
    }
}

export function* getProgramListStudentView(action: PayloadAction<ProgramTypes.GET_PROGRAM_LIST_STUDENT_VIEW_REQUEST, ProgramListStudentViewRequest>) {
    try {
        const {
            personId,
            classEnrollmentStatus,
            includeCourses,
            moduleIds,
            onlyCourseProgram,
            programIds,
            excludeExpiredAndNotFinished,
            pagination,
            section,
            categoryId,
        } = action.payload;
        const { offset, limit, orderBy, programRequestSuccessType, doRequest } = pagination;

        const path = Constants.LMS_GET_PROGRAM_LIST_STUDENT_VIEW.replace('%p', personId.toString());
        let queryParams: string = `?offset=${offset}&limit=${limit}`;

        if (orderBy !== undefined) {
            queryParams += `&orderBy=${orderBy}`;
        }

        if (classEnrollmentStatus !== undefined) {
            queryParams += `&classEnrollmentStatus=${classEnrollmentStatus}`;
        }

        if (includeCourses !== undefined) {
            queryParams += `&includeCourses=${includeCourses}`;
        }

        if (categoryId !== undefined && categoryId > 0) {
            queryParams += `&categoryId=${categoryId}`;
        }

        if (moduleIds !== undefined) {
            queryParams += `&moduleIds=${moduleIds}`;
        }

        if (onlyCourseProgram !== undefined) {
            queryParams += `&onlyCourseProgram=${onlyCourseProgram}`;
        }

        if (programIds !== undefined) {
            queryParams += `&programIds=${programIds}`;
        }

        if (section !== undefined) {
            queryParams += `&section=${section}`;
        }

        if (excludeExpiredAndNotFinished !== undefined) {
            queryParams += `&excludeExpiredAndNotFinished=${excludeExpiredAndNotFinished}`;
        }

        const request: AxiosRequestConfig = { headers: { requestSuccessType: programRequestSuccessType } };

        let response = { data: [], headers: { total: '0', totaltoexpire: '0' } };

        if (doRequest) {
            response = yield call(http.get, path + queryParams, request);
        }

        switch (request.headers.requestSuccessType) {
            case ProgramTypes.LAST_PUBLISHED_SUCCESS:
                yield put(programsLastPublishedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ProgramTypes.IN_PROGRESS_SUCCESS:
                yield put(programsInProgressSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ProgramTypes.COMPLETED_SUCCESS:
                yield put(programsCompletedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ProgramTypes.CATEGORIZED_SUCCESS:
                yield put(programsCategorizedSuccess(response.data, parseInt(response.headers.total), categoryId));
                break;
            case ProgramTypes.FAVORITE_SUCCESS:
                yield put(programsFavoriteSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ProgramTypes.ESSENTIALS_SUCCESS:
                yield put(programsEssentialSuccess(response.data, parseInt(response.headers.total), parseInt(response.headers.totaltoexpire)));
                break;
            case ProgramTypes.TRAININGS_SUCCESS:
                yield put(programsTrainingsSuccess(response.data, parseInt(response.headers.total)));
                break;
            default:
                break;
        }
    } catch (error) {
        yield put(getProgramListStudentViewFailure(error));
    }
}

export function* getProgramClassStudentView(
    action: PayloadAction<ProgramTypes.GET_PROGRAM_CLASS_STUDENT_VIEW_REQUEST, ProgramListStudentViewRequest>,
) {
    try {
        const { personId, classEnrollmentId, programId } = action.payload;
        const path = Constants.FLOWLMS_GET_PROGRAM_CLASS_STUDENT_VIEW.replace('%p', personId.toString())
            .replace('%c', classEnrollmentId.toString())
            .replace('%programId', programId.toString());
        const response = yield call(http.get, path);

        yield put(getProgramClassStudentViewSucess(response.data));
    } catch (error) {
        yield put(getProgramClassStudentViewFailure(error));
    }
}

export function* setProgramPrerequisitesRequest(
    action: PayloadAction<ProgramTypes.SET_PROGRAM_PREREQUISITES_REQUEST, { programId: number; isEnabled: boolean }>,
) {
    try {
        yield call(
            http.put,
            Constants.FLOWLMS_PROGRAM_SET_PREREQUISITES.replace('%p', action.payload.programId.toString()) +
                '?prerequisites=' +
                action.payload.isEnabled,
        );
        yield put(setProgramPrerequisitesSuccess());
    } catch (err) {
        yield put(setProgramPrerequisitesFailure(err));
    }
}

export function* refreshProgramsInProgress(personId: number, classEnrollmentId: number | undefined, modules: Module[]) {
    yield put(programsInProgressClear());
    yield call(getProgramListStudentView, {
        type: ProgramTypes.GET_PROGRAM_LIST_STUDENT_VIEW_REQUEST,
        payload: {
            personId,
            classEnrollmentId,
            classEnrollmentStatus: Constants.classEnrollmentStatus.NOT_COMPLETED,
            onlyCourseProgram: false,
            moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.LMS_MODULE_ID]),
            includeCourses: false,
            section: 'IN_PROGRESS',
            excludeExpiredAndNotFinished: true,
            pagination: {
                offset: 0,
                limit: Constants.COURSE_SECTION_PAGINATION_LIMIT,
                orderBy: 'p.statusDate desc',
                programRequestSuccessType: ProgramTypes.IN_PROGRESS_SUCCESS,
                doRequest: true,
            } as ProgramPaginationData,
        } as ProgramListStudentViewRequest,
    });
}

export function* refreshProgramsInLastPublished(personId: number, classEnrollmentId: number | undefined, modules: Module[]) {
    yield put(programsLastPublishedClear());
    yield call(getProgramListStudentView, {
        type: ProgramTypes.GET_PROGRAM_LIST_STUDENT_VIEW_REQUEST,
        payload: {
            personId,
            classEnrollmentId,
            onlyCourseProgram: false,
            moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.LMS_MODULE_ID]),
            includeCourses: false,
            excludeExpiredAndNotFinished: true,
            pagination: {
                offset: 0,
                limit: Constants.COURSE_SECTION_PAGINATION_LIMIT,
                orderBy: 'ce.enrollDate desc',
                programRequestSuccessType: ProgramTypes.LAST_PUBLISHED_SUCCESS,
                doRequest: true,
            } as ProgramPaginationData,
        } as ProgramListStudentViewRequest,
    });
}

export function* refreshProgramsFavorite(personId: number, classEnrollmentId: number | undefined, modules: Module[]) {
    yield put(programsFavoriteClear());

    const favoriteProgramList: string = yield select((state: ApplicationState) =>
        courseService.getFavoriteEntityListAsIdsString(state.favorite.favoriteList, Constants.entityTypes.PROGRAM),
    );

    yield call(getProgramListStudentView, {
        type: ProgramTypes.GET_PROGRAM_LIST_STUDENT_VIEW_REQUEST,
        payload: {
            personId,
            classEnrollmentId,
            onlyCourseProgram: false,
            moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.LMS_MODULE_ID]),
            includeCourses: false,
            programIds: favoriteProgramList,
            pagination: {
                offset: 0,
                limit: Constants.COURSE_SECTION_PAGINATION_LIMIT,
                orderBy: 'ce.enrollDate desc',
                programRequestSuccessType: ProgramTypes.FAVORITE_SUCCESS,
                doRequest: favoriteProgramList.length > 0,
            } as ProgramPaginationData,
        } as ProgramListStudentViewRequest,
    });
}
export function* refreshProgramsEssentials(personId: number, classEnrollmentId: number | undefined, modules: Module[]) {
    yield put(programsEssentialClear());
    yield call(getProgramListStudentView, {
        type: ProgramTypes.GET_PROGRAM_LIST_STUDENT_VIEW_REQUEST,
        payload: {
            personId,
            classEnrollmentId,
            onlyCourseProgram: false,
            moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.LMS_MODULE_ID]),
            includeCourses: false,
            section: 'MANDATORY',
            pagination: {
                offset: 0,
                limit: Constants.COURSE_SECTION_PAGINATION_LIMIT,
                programRequestSuccessType: ProgramTypes.ESSENTIALS_SUCCESS,
                doRequest: true,
            } as ProgramPaginationData,
        } as ProgramListStudentViewRequest,
    });
}

export function* setProgramReady(action: PayloadAction<ProgramTypes.SET_PROGRAM_READY_REQUEST, number>) {
    try {
        yield call(http.post, `${Constants.LMS_PROGRAMS_URI}/${action.payload}/terminate`);
        yield put(setProgramReadySuccess(action.payload));
    } catch (err) {
        yield put(setProgramReadyFailure(err));
    }
}

export function* getProgramSearchedRequest(action: PayloadAction<ProgramTypes.SEARCHED_REQUEST, ProgramSearchedRequest>) {
    try {
        const {
            term,
            moduleIds,
            pagination: { offset, limit },
            locales,
        } = action.payload;

        const { data, headers } = yield call(http.get, Constants.CAST_PROGRAMS_SEARCH_URI, { params: { offset, limit, term, moduleIds, locales } });

        yield put(getProgramSearchedSuccess(data, parseInt(headers.total, 10)));
    } catch (error) {
        yield put(getProgramSearchedFailure(error));
    }
}

export function* updateProgramClassStudentView(
    action: PayloadAction<ProgramTypes.UPDATE_PROGRAM_CLASS_STUDENT_VIEW_REQUEST, ProgramListStudentViewRequest>,
) {
    try {
        const { personId, classEnrollmentId, programId } = action.payload;
        const path = Constants.FLOWLMS_GET_PROGRAM_CLASS_STUDENT_VIEW.replace('%p', personId.toString())
            .replace('%c', classEnrollmentId.toString())
            .replace('%programId', programId.toString());
        const { data } = yield call(http.get, path);

        yield put(updateProgramClassStudentViewSuccess(data));
    } catch (error) {
        yield put(updateProgramClassStudentViewFailure(error));
    }
}

export function* increaseProgramViewsRequest(action: PayloadAction<ProgramTypes.INCREASE_PROGRAM_VIEWS_REQUEST, number>) {
    try {
        yield call(http.post, Constants.LMS_PROGRAM_VIEWS.replace('%programId', action.payload.toString()));
        yield put(increaseProgramViewsSuccess());
    } catch (error) {
        yield put(increaseProgramViewsFailure());
    }
}
